import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import { Message, MessageBox } from 'element-ui'
import ElementUI from 'element-ui'
import 'element-ui/lib/theme-chalk/index.css'
import './styles/yjsy-variables.css'
// 领导关怀等 v-html 正文依赖此样式（原先仅在 Quill 懒加载时引入，导致详情页无行内 color/对齐）
import './assets/rich-text-fix.css'
import './assets/rich-text-wechat-display.css'
import './styles/leadership-care-detail-content.css'
import ErrorHandler from './utils/errorHandler'
import PerformanceMonitor from './utils/performance'
import PerformanceOptimizer from './utils/performance-optimizer'
import request from './utils/request'
import { safeLocalStorageGet } from './utils'

// 设置全局路由错误处理
import { setupRouterErrorHandler } from './utils/router-error-handler'
setupRouterErrorHandler(router)

// 注册全局路由安全跳转mixin
import routerSafeMixin from './mixins/router-safe'
Vue.mixin(routerSafeMixin)

// 富文本编辑器延迟加载 - 提升首屏速度
// 延迟加载Quill，避免阻塞首屏
let quillLoaded = false
const loadQuill = () => {
  if (quillLoaded) return Promise.resolve()
  return import('vue-quill-editor').then(module => {
    const VueQuillEditor = module.default
    // 动态导入CSS
    import('quill/dist/quill.core.css')
    import('quill/dist/quill.snow.css')
    import('quill/dist/quill.bubble.css')
    import('./assets/rich-text-fix.css')

    // 配置Quill编辑器全局选项
    VueQuillEditor.options = {
      ...VueQuillEditor.options,
      bounds: document.body,
      scrollingContainer: document.body
    }
    Vue.use(VueQuillEditor)
    quillLoaded = true
  })
}

// 为了支持富文本内容显示，预加载Quill的CSS文件
// 即使不使用编辑器，也需要CSS来正确显示字体大小等样式
import('quill/dist/quill.core.css')
import('quill/dist/quill.snow.css')

// 在路由守卫中，访问需要富文本编辑器的页面时预加载
router.beforeEach((to, from, next) => {
  // 需要富文本编辑器的路由
  const quillRoutes = ['/user-center', '/write-user-center', '/publish-product', '/publish-demand', '/invite-expert', '/my-products']
  if (quillRoutes.some(route => to.path.includes(route) || to.path.startsWith(route))) {
    loadQuill().finally(() => next())
  } else {
    next()
  }
})

Vue.config.productionTip = false

// Element UI全量引入 - 这是首屏加载慢的主要原因，但按需引入需要额外配置
// 考虑未来优化为按需引入（需要babel-plugin-component）
Vue.use(ElementUI)
Vue.use(ErrorHandler)
Vue.use(PerformanceMonitor)
Vue.use(PerformanceOptimizer)

// 配置全局HTTP请求
Vue.prototype.$http = request

// 配置全局消息提示
Vue.prototype.$message = function(options) {
  return Message(options);
};
Vue.prototype.$message.success = function (message) {
  return Message.success({
    message,
    showClose: true  // 设置默认显示关闭按钮
  });
};
Vue.prototype.$message.info = function (message) {
  return Message.info({
    message,
    showClose: true  // 设置默认显示关闭按钮
  });
};
Vue.prototype.$message.error = function (message) {
  return Message.error({
    message,
    showClose: true  // 设置默认显示关闭按钮
  });
};
Vue.prototype.$message.warning = function (message) {
  return Message.warning({
    message,
    showClose: true  // 设置默认显示关闭按钮
  });
};

// 全局图片预览方法
Vue.prototype.$preview = function(options) {
  // 使用 MessageBox.alert 创建图片预览弹窗
  const imagePreview = `
    <div style="text-align: center; padding: 20px;">
      <img 
        src="${options.url}" 
        alt="${options.title || '图片预览'}"
        style="max-width: 100%; max-height: 70vh; object-fit: contain; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15);"
        onerror="this.style.display='none'; this.nextElementSibling.style.display='block';"
      />
      <div style="display: none; color: #999; font-size: 14px;">
        图片加载失败，请检查图片地址
      </div>
    </div>
  `;

  // 直接使用 MessageBox.alert，不依赖 this.$alert
  return MessageBox.alert(imagePreview, options.title || '图片预览', {
    dangerouslyUseHTMLString: true,
    confirmButtonText: '关闭',
    customClass: 'image-preview-dialog',
    center: true
  }).catch(() => {
    // 静默处理关闭操作
  });
};

// 注册全局混入，但只对路由组件生效
Vue.mixin({
  beforeRouteEnter(to, from, next) {
    next(() => {
      // 确保在路由进入时滚动到顶部
      window.scrollTo(0, 0)
    })
  },
  
  beforeRouteUpdate(to, from, next) {
    // 当路由参数变化但组件复用时也滚动到顶部
    next()
    this.$nextTick(() => {
      window.scrollTo(0, 0)
    })
  }
})

// 初始化跨应用登录状态同步
import AuthSync from './utils/auth-sync'
const authSync = new AuthSync(store, 'pc')
store.commit('SET_AUTH_SYNC', authSync)

// 应用启动时自动同步用户信息（解决刷新后状态丢失）
// 使用安全读取，避免浏览器「跟踪防护」阻断 storage 时抛错
const token = (safeLocalStorageGet('token') || '').trim()
if (token) {
  // 立即恢复token到store，确保请求拦截器能获取到token
  store.commit('auth/SET_TOKEN', token)
  
  // 异步同步用户信息，不阻塞页面渲染
  // 使用Promise.resolve().then()确保在下一个事件循环中执行
  Promise.resolve().then(() => {
    store.dispatch('auth/syncUser').catch(error => {
      // 静默处理所有错误，避免影响页面加载
      // 401错误可能是token过期，由syncUser内部处理
      if (error.response?.status !== 401) {
        console.error('用户信息同步失败:', error)
      }
    })
  })
}

// 访问统计将在首页加载时自动记录

new Vue({
  router,
  store,
  render: h => h(App)
}).$mount('#app') 